import { useEffect, useState } from "react";
import { CheckCircle2, Clock, XCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS_CFG = {
  pending:  { label: "Pending",  cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Clock },
  verified: { label: "Verified", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  rejected: { label: "Rejected", cls: "bg-red-50 text-red-700 border-red-200", Icon: XCircle },
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
}

export default function DepositForm({ method, onSubmitted }) {
  const [utr, setUtr] = useState("");
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!(amt > 0)) return toast.error("Enter a valid amount");
    if (utr.trim().length < 6) return toast.error("Enter a valid UTR number");
    setSubmitting(true);
    try {
      await api.post("/deposits", {
        method,
        account: "State Bank of India",
        utr_number: utr.trim(),
        amount: amt,
      });
      toast.success("Deposit request submitted — waiting for admin verification");
      setUtr(""); setAmount("");
      onSubmitted?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      data-testid="deposit-form"
      onSubmit={submit}
      className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4"
    >
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">UTR No</label>
        <input
          data-testid="deposit-utr-input"
          value={utr}
          onChange={(e) => setUtr(e.target.value)}
          placeholder="Enter UTR number"
          className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-red-500 outline-none"
          required
          minLength={6}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Amount</label>
        <input
          data-testid="deposit-amount-input"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          type="number"
          inputMode="decimal"
          min="1"
          step="0.01"
          placeholder="Enter amount"
          className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-red-500 outline-none"
          required
        />
      </div>
      <button
        data-testid="deposit-submit-btn"
        type="submit"
        disabled={submitting}
        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg py-3 transition-colors active:scale-[0.99] disabled:opacity-60"
      >
        {submitting ? "Submitting..." : "Submit"}
      </button>
    </form>
  );
}

export function DepositHistory({ reloadKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/deposits/my");
        if (!cancelled) setItems(data.items || []);
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  return (
    <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div
        className="px-5 py-4 text-white font-bold"
        style={{
          background: "linear-gradient(90deg, #3b82f6 0%, #8b5cf6 50%, #a855f7 100%)",
          fontFamily: "Outfit, sans-serif",
        }}
      >
        Fund Deposit History
      </div>
      {loading ? (
        <div className="p-6 flex justify-center">
          <div className="h-6 w-6 rounded-full border-4 border-red-600 border-t-transparent animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div data-testid="deposit-history-empty" className="p-10 text-center text-slate-500">
          No fund deposits found
        </div>
      ) : (
        <ul data-testid="deposit-history-list" className="divide-y divide-slate-100">
          {items.map((d) => {
            const cfg = STATUS_CFG[d.status] || STATUS_CFG.pending;
            const Icon = cfg.Icon;
            return (
              <li key={d.id} className="p-4 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-slate-900">
                      ₹{Number(d.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </span>
                    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${cfg.cls}`}>
                      <Icon size={12} /> {cfg.label}
                    </span>
                    <span className="text-[11px] text-slate-500 uppercase tracking-wider">
                      {d.method === "qr" ? "QR-Code" : "Bank"}
                    </span>
                  </div>
                  <div className="text-xs text-slate-600 mt-1 truncate">
                    UTR: <span className="font-mono">{d.utr_number}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{formatDate(d.created_at)}</div>
                  {d.admin_note && (
                    <div className="text-[11px] text-slate-500 italic mt-1">Note: {d.admin_note}</div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
